import React, { useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { AutoComplete, AutoCompleteField, Datepicker } from '../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { formatStartDate, getRowDictionary, getRowScopeKey } from '../utils';
import {
    COLLAPSED_FIELDS,
    NON_CLEARABLE_FIELDS,
    PER_ROW_DICTS,
    TRANSLATION_PREFIX,
    VALUE_CURRENCY_FIELD,
} from '../consts';
import css from '../page.css';

const ALWAYS_LABEL = `${TRANSLATION_PREFIX}.ALWAYS`;

// The picker has no vertical auto-placement, so the direction is chosen before it renders. The
// estimate is only used until a calendar has been measured once.
const ESTIMATED_CALENDAR_HEIGHT_PX = 340;
let measuredCalendarHeightPx = 0;

const calendarHeight = () => measuredCalendarHeightPx || ESTIMATED_CALENDAR_HEIGHT_PX;

const rememberCalendarHeight = () => {
    // appendToBody puts it on <body>, so it is found by class rather than through the row.
    const calendar = document.querySelector('.daterangepicker');
    if (calendar && calendar.offsetHeight) {
        measuredCalendarHeightPx = calendar.offsetHeight;
    }
};

const isEmptyCellValue = value =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

// A dictionary-backed grid cell. Uses the bare AutoComplete (not AutoCompleteField) so the cell
// has no label/error-text rows — errors surface as a red border, like the legacy grid.
const DictCell = ({
    field,
    placeholder,
    cell,
    options,
    dictionary,
    error,
    disabled,
    isClearable = true,
    isLoading = false,
    isPerRow = false,
    onChange,
    onMenuOpen,
    onInputChange,
}) => (
    <AutoComplete
        // Remount when the resolved cell changes so the control reflects external updates
        // (import, reload, row deletion shift) without becoming a stale controlled input.
        key={`${field}-${cell ? cell.name : ''}`}
        placeholder={placeholder}
        // The select has no visible label; name it by its column, as the filter inputs do.
        ariaLabel={field}
        defaultValue={cell ? { value: cell.name, label: cell.display_name } : null}
        onChange={option => onChange(field, option, dictionary)}
        onInputChange={onInputChange}
        // Per-row options only exist after the cell is opened, so the indicator has to be forced -
        // otherwise these two columns are the only ones with no visible dropdown affordance.
        alwaysShowDropdownIndicator={isPerRow}
        error={error ? 'error' : ''}
        searchable
        selectOptions={{
            options,
            disabled,
            isClearable,
            isSearchable: true,
            isLoading,
            noOptionsMessage: () => null,
            // Portal the menu to <body> so it isn't clipped by the grid's overflow / z-index,
            // and open it upward when there's no room below (e.g. the last rows).
            usePortal: true,
            menuPlacement: 'auto',
            // Passed through selectOptions because AutoComplete owns onFocus itself. Opening the
            // cell is what fetches, not rendering the row, so a large table loads without requests.
            onMenuOpen,
        }}
    />
);

DictCell.propTypes = {
    field: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    cell: PropTypes.shape({ name: PropTypes.string, display_name: PropTypes.string }),
    options: PropTypes.arrayOf(PropTypes.object),
    dictionary: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    isClearable: PropTypes.bool,
    isLoading: PropTypes.bool,
    isPerRow: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onMenuOpen: PropTypes.func,
    onInputChange: PropTypes.func,
};

const MultiDictCell = ({ field, placeholder, cells, options, dictionary, error, disabled, onChange }) => {
    const value = (cells || []).map(cell => ({ value: cell.name, label: cell.display_name }));
    return (
        <AutoCompleteField
            label=""
            labelClassName={css.hiddenLabel}
            containerClass={css.multiDictCell}
            isMulti
            clearable
            searchable
            placeholder={placeholder}
            value={value}
            options={options || []}
            error={error ? 'error' : ''}
            errorClassName={css.hiddenErrorMessage}
            disabled={disabled}
            onChange={selected => onChange(field, selected || [], dictionary)}
        />
    );
};

MultiDictCell.propTypes = {
    field: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    cells: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, display_name: PropTypes.string })),
    options: PropTypes.arrayOf(PropTypes.object),
    dictionary: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
};

function ReferenceRow({
    row,
    visibleColumns,
    dictionaries,
    dictionaryOptions,
    perRowDictionaries,
    isValueCurrencyEnabled,
    errors,
    disabled,
    onCellChange,
    onDateChange,
    onValueChange,
}) {
    const [editingDate, setEditingDate] = useState(false);
    const [dropDirection, setDropDirection] = useState('down');
    const dateCellRef = useRef(null);
    const rowErrors = errors || {};

    // appendToBody takes the calendar out of the grid, so opening down on the last rows extends the page.
    const toggleDateEditing = () => {
        if (!editingDate && dateCellRef.current) {
            const { bottom } = dateCellRef.current.getBoundingClientRect();
            setDropDirection(window.innerHeight - bottom < calendarHeight() ? 'up' : 'down');
        }
        setEditingDate(editing => !editing);
    };

    // One open is enough to learn the real height.
    useLayoutEffect(() => {
        if (editingDate) {
            rememberCalendarHeight();
        }
    }, [editingDate]);

    // Datepicker builds its calendar from moment(startDate); an empty value renders a NaN calendar,
    // so a dateless row opens on today. The row value stays null until a date is applied.
    const pickerDate = row.startDate || moment().format('YYYY-MM-DD');

    return (
        <tr className={css.gridRow}>
            <td className={css.dateCell} ref={dateCellRef}>
                <div className={css.dateCellInner}>
                    {editingDate ? (
                        <>
                            <Datepicker
                                singleDatePicker
                                autoApply
                                appendToBody
                                drops={dropDirection}
                                ranges={{}}
                                containerClass={css.datePicker}
                                startDate={pickerDate}
                                endDate={pickerDate}
                                onApply={({ start_date: startDate }) => {
                                    onDateChange(startDate);
                                    setEditingDate(false);
                                }}
                            />
                            <button
                                type="button"
                                className={css.dateAlwaysButton}
                                title="Clear the date (rule applies always)"
                                onClick={() => {
                                    onDateChange(null);
                                    setEditingDate(false);
                                }}
                            >
                                <Translate id={ALWAYS_LABEL} />
                            </button>
                        </>
                    ) : (
                        <span className={css.dateText}>
                            {row.startDate ? formatStartDate(row.startDate, '') : <Translate id={ALWAYS_LABEL} />}
                        </span>
                    )}
                    <button
                        type="button"
                        className={css.editDateButton}
                        title={editingDate ? 'Done' : 'Edit date'}
                        disabled={disabled}
                        onClick={toggleDateEditing}
                    >
                        <Icon
                            name={editingDate ? 'check' : 'pencil'}
                            variant={IconVariants.LIGHT}
                            size={IconSizes.SMALL}
                        />
                    </button>
                </div>
            </td>

            {visibleColumns.map(column => {
                if (COLLAPSED_FIELDS.includes(column.field)) {
                    return (
                        <td key={column.field} className={css.dictCell}>
                            <MultiDictCell
                                field={column.field}
                                placeholder={column.placeholder}
                                cells={row.country}
                                options={getRowDictionary(dictionaryOptions, column.dict, row)}
                                dictionary={getRowDictionary(dictionaries, column.dict, row)}
                                error={!!rowErrors[column.field]}
                                disabled={disabled}
                                onChange={onCellChange}
                            />
                        </td>
                    );
                }
                // Per-row columns resolve through the row and load on open; `dict` must equal
                // `field` for them, as it does for every column.
                const isPerRow = !!PER_ROW_DICTS[column.dict];
                // A column can ask for a different placeholder - and to be closed - while the cells it
                // narrows by are empty.
                const hint = column.hintWhenEmpty;
                // `![]` is false, so a bare falsy check would read an empty array-valued dependency as
                // populated and never fire; treat [] and '' as empty too.
                const hintApplies = !!hint && hint.fields.every(field => isEmptyCellValue(row[field]));
                const placeholder = hintApplies ? hint.placeholder : column.placeholder;
                // Never disable a cell that already holds a value: a row saved before its source was
                // cleared would otherwise be stuck, unable to clear or correct the stale choice.
                const closedUntilScoped = hintApplies && !!hint.disable && !row[column.field];
                return (
                    <td key={column.field} className={css.dictCell}>
                        <DictCell
                            field={column.field}
                            placeholder={placeholder}
                            cell={row[column.field]}
                            options={getRowDictionary(dictionaryOptions, column.dict, row)}
                            dictionary={getRowDictionary(dictionaries, column.dict, row)}
                            error={!!rowErrors[column.field]}
                            disabled={disabled || closedUntilScoped}
                            isLoading={
                                isPerRow &&
                                !!perRowDictionaries.loading[`${column.dict}:${getRowScopeKey(column.dict, row)}`]
                            }
                            isPerRow={isPerRow}
                            isClearable={!NON_CLEARABLE_FIELDS.includes(column.field)}
                            onChange={onCellChange}
                            onMenuOpen={isPerRow ? () => perRowDictionaries.onOpen(column.dict, row) : undefined}
                            onInputChange={
                                isPerRow ? query => perRowDictionaries.onSearch(column.dict, row, query) : undefined
                            }
                        />
                    </td>
                );
            })}

            <td className={css.valueCell}>
                <input
                    className={`${css.valueInput} ${rowErrors.value ? css.inputError : ''}`}
                    type="text"
                    placeholder="Number or 'Skip'"
                    disabled={disabled}
                    value={row.value}
                    onChange={event => onValueChange(event.target.value)}
                />
            </td>

            {isValueCurrencyEnabled && (
                <td className={`${css.dictCell} ${css.valueCurrencyCell}`}>
                    <DictCell
                        field={VALUE_CURRENCY_FIELD}
                        placeholder={`${TRANSLATION_PREFIX}.COLUMNS.CURRENCY`}
                        cell={row[VALUE_CURRENCY_FIELD]}
                        options={dictionaryOptions[VALUE_CURRENCY_FIELD]}
                        dictionary={dictionaries[VALUE_CURRENCY_FIELD]}
                        error={!!rowErrors[VALUE_CURRENCY_FIELD]}
                        disabled={disabled}
                        // Required field — no clear control (empty fails validation and it crowds the cell).
                        isClearable={false}
                        onChange={onCellChange}
                    />
                </td>
            )}

            <td className={css.spacerCell} />
        </tr>
    );
}

ReferenceRow.propTypes = {
    row: PropTypes.shape({
        _key: PropTypes.string,
        id: PropTypes.number,
        startDate: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        country: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    visibleColumns: PropTypes.arrayOf(
        PropTypes.shape({ field: PropTypes.string, placeholder: PropTypes.string, dict: PropTypes.string })
    ).isRequired,
    // Per-row dictionaries are keyed maps, so an entry is either shape.
    dictionaries: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
    dictionaryOptions: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
    perRowDictionaries: PropTypes.shape({
        loading: PropTypes.objectOf(PropTypes.bool).isRequired,
        onOpen: PropTypes.func.isRequired,
        onSearch: PropTypes.func.isRequired,
    }).isRequired,
    isValueCurrencyEnabled: PropTypes.bool,
    errors: PropTypes.objectOf(PropTypes.bool),
    disabled: PropTypes.bool,
    onCellChange: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
};

export default ReferenceRow;
